"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

import { cn } from "@/lib/utils"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        ),
        info: (
          <InfoIcon className="size-4 shrink-0 text-muted-foreground" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        ),
        error: (
          <OctagonXIcon className="size-4 shrink-0 text-destructive" />
        ),
        loading: (
          <Loader2Icon className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          fontFamily: "var(--font-sans)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: cn(
            "cn-toast !gap-3 !rounded-xl !border-border !bg-popover !px-4 !py-3 !text-sm !font-medium !leading-snug !text-foreground !shadow-lg !ring-1 !ring-foreground/10",
            "[&_[data-description]]:!mt-0 [&_[data-description]]:!text-sm [&_[data-description]]:!font-normal [&_[data-description]]:!leading-snug [&_[data-description]]:!text-muted-foreground"
          ),
          title: "!text-foreground",
          closeButton: cn(
            "!size-7 !border-border !bg-background !text-muted-foreground hover:!border-border hover:!bg-muted hover:!text-foreground"
          ),
          actionButton:
            "!h-8 !rounded-md !border-0 !bg-primary !px-3 !text-xs !font-medium !text-primary-foreground hover:!bg-primary/90",
          cancelButton:
            "!h-8 !rounded-md !border-0 !bg-muted !px-3 !text-xs !font-medium !text-muted-foreground hover:!bg-muted/80",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
